import { useState, useEffect, useMemo } from 'react'
import { createAppTheme } from '../configurations/theme'

type PaletteMode = 'light' | 'dark'

const THEME_STORAGE_KEY = 'kundenportal-theme-mode'

export function useThemeMode() {
  // Lade initiales Theme aus localStorage oder System-Präferenz
  const getInitialMode = (): PaletteMode => {
    const savedMode = localStorage.getItem(THEME_STORAGE_KEY)
    if (savedMode === 'light' || savedMode === 'dark') {
      return savedMode
    }
    // System-Präferenz prüfen
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    }
    return 'light'
  }

  const [mode, setMode] = useState<PaletteMode>(getInitialMode)

  // Theme bei Änderung speichern
  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, mode)
    // Metadaten für Browser aktualisieren
    document.documentElement.setAttribute('data-theme', mode)
  }, [mode])

  // System-Präferenz-Änderungen beobachten
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      // Nur ändern, wenn kein manuell gesetztes Theme vorhanden
      if (!localStorage.getItem(THEME_STORAGE_KEY)) {
        setMode(e.matches ? 'dark' : 'light')
      }
    }
    
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Theme-Objekt memoizen
  const theme = useMemo(() => createAppTheme(mode), [mode])

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'))
  }

  const setThemeMode = (newMode: PaletteMode) => {
    setMode(newMode)
  }

  return {
    mode,
    theme,
    toggleTheme,
    setThemeMode,
    isDarkMode: mode === 'dark'
  }
}

export default useThemeMode
